import { LAST_UPDATED } from './legalMeta'

/**
 * Cookie Policy — an honest inventory, not a boilerplate list.
 *
 * Verified against the code:
 *  - `__astro_session`: encrypted JWE, httpOnly, SameSite=Lax, 8h TTL, set on
 *    the parent domain in production (`apps/auth-bff/src/session.ts`, `env.ts`).
 *  - localStorage: `astro-cart:<env>` (zustand persist, `packages/store`),
 *    `shop:cart` (`packages/storage/src/typed-store.ts`),
 *    `astro-dictation-lang` (DictationButton), `livekit:tab-id` (classroom).
 *  - Analytics: gtag, production builds only (`packages/analytics`).
 *  - We ship no consent banner today, which section 6 states plainly instead of
 *    pretending otherwise.
 */
export const COOKIE_POLICY = {
  slug: 'cookie-policy',
  badge: 'Legal',
  title: 'Cookie Policy',
  intro:
    'We use very few cookies, and this page lists all of them by name — what each one does, how long it lasts, and how to switch off the ones that are optional.',
  updated: LAST_UPDATED,
  summary: [
    'We set exactly one cookie of our own: the sign-in session cookie. Without it you cannot stay signed in.',
    'Your cart and a couple of interface preferences are kept in your browser local storage, not sent to us as cookies.',
    'In production we use Google Analytics to count traffic in aggregate. We never send your name, email, phone or birth details to it.',
    'Embedded videos, maps, fonts and the payment checkout are loaded from third parties, which may set their own cookies.',
    'We do not use advertising or retargeting cookies, and we do not sell data to ad networks.',
  ],
  sections: [
    {
      heading: '1. What cookies and local storage are',
      body: [
        'A cookie is a small text file a website asks your browser to keep, and sends back on your next request. Local storage and session storage are similar, except that they stay in your browser and are only read by code running on the page — they are not automatically transmitted anywhere.',
        'This policy covers both, and forms part of our Privacy Policy.',
      ],
    },
    {
      heading: '2. The cookie we set',
      body: [
        'We set one cookie of our own.',
      ],
      subsections: [
        {
          heading: '__astro_session — your sign-in session',
          list: [
            'What it does — keeps you signed in. It holds your session in encrypted form so that the access token behind it never reaches your browser or any script running in it.',
            'Why it is needed — without it you cannot sign in, see your dashboard, book a consultation, enrol in a course or check out.',
            'How long it lasts — eight hours, after which you sign in again.',
            'Scope — it is set on our parent domain, which is what lets one sign-in work across our website, consultation portal, shop and academy. Signing out clears it everywhere at once.',
            'Protections — it is marked httpOnly, so page scripts cannot read it; it is sent only over HTTPS in production; and it uses SameSite=Lax, which stops it being sent along with requests started by other websites.',
            'Category — strictly necessary. It is not used for analytics or advertising and it cannot be switched off while you are signed in.',
          ],
        },
      ],
    },
    {
      heading: '3. What we keep in your browser local storage',
      body: [
        'These stay on your device, are not cookies, and are not transmitted to us as part of a request. Clearing your browser site data removes them.',
      ],
      list: [
        'Your shopping cart — so the items you picked are still there when you come back or move between our sites. It holds the products and quantities, not payment details.',
        'Dictation language — for astrologers using voice typing for consultation notes, the language last chosen.',
        'Classroom tab identifier — a random value that lets a live classroom tell two browser tabs apart, so joining a class twice does not knock you out of it.',
      ],
    },
    {
      heading: '4. Analytics',
      body: [
        'On our production sites we use Google Analytics to understand how our pages are used in aggregate — which pages are visited, how many people sign in, items added to a cart, checkouts started and course enrolments. It sets its own cookies to tell a returning browser from a new one.',
        'We use it to make the site better. We do not send it your name, email address, phone number, delivery address or birth details, we do not use it to profile you individually, and we do not use it to set different prices for different people.',
        'Analytics is not loaded in our development or test builds.',
      ],
    },
    {
      heading: '5. Third-party cookies you may encounter',
      body: [
        'Some pages load resources from other companies, and your browser contacts those companies directly when it does. They receive your IP address and request details, and may set their own cookies under their own policies, which we do not control.',
      ],
      list: [
        'Payment checkout — our payment gateway loads its own checkout to collect your card or UPI details securely.',
        'Video calling and live classes — our video infrastructure providers, while a consultation or class is running.',
        'Embedded videos — YouTube and Vimeo players on course and content pages.',
        'Maps — an embedded map on our contact page.',
        'Fonts and icons — Google Fonts and a public content-delivery network for icons.',
        'Images — our media storage and image delivery services.',
        'Social links — where you click through to a social network, that network sets its own cookies on its own site.',
      ],
      note: 'We do not run advertising, retargeting or data-broker pixels, and we do not share your personal data with ad networks.',
    },
    {
      heading: '6. Your choices',
      body: [
        'We do not currently show a cookie consent banner, because the only cookie we set ourselves is the strictly necessary sign-in cookie. If we introduce cookies that need your consent under applicable law, we will ask for it before setting them, and update this page.',
        'You remain in control in the meantime:',
      ],
      list: [
        'Block or delete cookies in your browser settings. Blocking our session cookie means you cannot stay signed in, and checkout and your dashboard will not work.',
        'Clear your site data to remove the local storage items in section 3, including your saved cart.',
        'Opt out of Google Analytics across all sites by installing Google’s own browser opt-out add-on.',
        'Turn on "Do Not Track" or a similar privacy signal in your browser. There is no agreed standard for how sites must respond to it, so we do not claim to honour it; what we do with your data is set out in our Privacy Policy regardless.',
        'Use a private or incognito window, which discards cookies and local storage when you close it.',
      ],
    },
    {
      heading: '7. Changes to this policy',
      body: [
        'We will update this page when we add, remove or change a cookie, a storage item or an analytics tool. The "Last updated" date at the top tells you when it last changed.',
      ],
    },
    {
      heading: '8. Contact us',
      body: [
        'If you have a question about anything on this page, or you believe a cookie is being set that is not listed here, tell us{contact} and we will look into it.',
      ],
    },
  ],
}
